import React, { useMemo, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Chip,
  Divider,
  Snackbar,
  Alert,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Close as CloseIcon,
  TableChart as TableChartIcon,
  FilterList as FilterListIcon,
  KeyboardArrowUp as ExpandIcon,
  KeyboardArrowDown as CollapseIcon,
  Undo as UndoIcon,
  Redo as RedoIcon,
} from '@mui/icons-material';
import {
  DataGrid,
  type GridColDef,
  type GridRowsProp,
  type GridSortModel,
  type GridFilterModel,
  type GridRowSelectionModel,
  type GridRowModel,
} from '@mui/x-data-grid';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import { selectSelectedFeatures } from '../store/slices/selectionSlice';
import { updateFeatureAttribute } from '../store/slices/layersSlice';
import { recordEdit } from '../store/slices/attributeHistorySlice';
import { useAttributeUndoRedo } from '../hooks/useAttributeUndoRedo';
import type { RootState } from '../store';

/**
 * Height presets for the attribute table panel.
 */
const TABLE_HEIGHT_COLLAPSED = 36;
const TABLE_HEIGHT_DEFAULT = 280;
const TABLE_HEIGHT_EXPANDED = 480;

/**
 * Checks if a value represents a valid date.
 * Accepts Date objects and ISO 8601 date strings.
 */
export function isDateValue(value: any): boolean {
  if (value instanceof Date) return !isNaN(value.getTime());
  if (typeof value === 'string') {
    // Check for common date patterns (ISO 8601, YYYY-MM-DD, etc.)
    const datePatterns = [
      /^\d{4}-\d{2}-\d{2}$/, // YYYY-MM-DD
      /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/, // ISO 8601 with time
      /^\d{4}\/\d{2}\/\d{2}$/, // YYYY/MM/DD
      /^\d{2}\/\d{2}\/\d{4}$/, // MM/DD/YYYY
      /^\d{2}-\d{2}-\d{4}$/, // MM-DD-YYYY or DD-MM-YYYY
    ];
    if (datePatterns.some((pattern) => pattern.test(value))) {
      const parsed = new Date(value);
      return !isNaN(parsed.getTime());
    }
  }
  return false;
}

/**
 * Detects the original data type of a field by examining existing feature values.
 * Returns 'number', 'boolean', 'date', or 'string'.
 */
export function detectFieldType(
  features: Array<{ properties?: Record<string, any> }>,
  field: string
): 'number' | 'boolean' | 'date' | 'string' {
  for (const feature of features) {
    const value = feature.properties?.[field];
    if (value === null || value === undefined) continue;
    if (typeof value === 'number') return 'number';
    if (typeof value === 'boolean') return 'boolean';
    if (value instanceof Date) return 'date';
    if (typeof value === 'string' && isDateValue(value)) return 'date';
    return 'string';
  }
  return 'string';
}

/**
 * Validates that a new value is compatible with the expected data type.
 * Returns the coerced value if valid, or throws an error if invalid.
 */
export function validateAttributeValue(
  value: any,
  expectedType: 'number' | 'boolean' | 'date' | 'string'
): any {
  if (expectedType === 'number') {
    // If already a number, it's valid
    if (typeof value === 'number' && !isNaN(value)) return value;
    // If it's a string, try to parse it
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed === '') {
        throw new Error('Value cannot be empty for a numeric field');
      }
      const parsed = Number(trimmed);
      if (isNaN(parsed)) {
        throw new Error(`Invalid value "${value}" for numeric field. Expected a number.`);
      }
      return parsed;
    }
    throw new Error(`Invalid value for numeric field. Expected a number.`);
  }

  if (expectedType === 'boolean') {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') {
      const lower = value.trim().toLowerCase();
      if (lower === 'true') return true;
      if (lower === 'false') return false;
      throw new Error(`Invalid value "${value}" for boolean field. Expected "true" or "false".`);
    }
    throw new Error(`Invalid value for boolean field. Expected true or false.`);
  }

  if (expectedType === 'date') {
    if (value instanceof Date) {
      if (isNaN(value.getTime())) {
        throw new Error('Invalid date value.');
      }
      return value.toISOString();
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed === '') {
        throw new Error('Value cannot be empty for a date field');
      }
      const parsed = new Date(trimmed);
      if (isNaN(parsed.getTime())) {
        throw new Error(
          `Invalid value "${value}" for date field. Expected a valid date (e.g., "2024-01-15" or "2024-01-15T10:30:00Z").`
        );
      }
      return parsed.toISOString();
    }
    throw new Error(
      `Invalid value for date field. Expected a valid date string (e.g., "2024-01-15").`
    );
  }

  // String type: validate non-null and proper string type
  if (expectedType === 'string') {
    if (value === null || value === undefined) {
      throw new Error('Value cannot be null for a string field');
    }
    if (typeof value === 'string') {
      return value;
    }
    // Coerce other types to string
    return String(value);
  }

  return String(value ?? '');
}

export interface AttributeTableProps {
  /** Whether the attribute table panel is visible */
  open: boolean;
  /** Callback to close the attribute table */
  onClose: () => void;
  /** Optional fixed height override */
  height?: number;
  /** Whether to hide the internal header */
  hideHeader?: boolean;
}

/**
 * AttributeTable component displays feature attributes in a data grid.
 *
 * Behavior (per Requirements 12 & 13):
 * - When features are selected, displays attributes for selected features only
 * - When no features are selected, displays all features in the active layer
 * - Supports column sorting
 * - Supports row filtering by attribute values
 *
 * @component
 */
/** MUI X DataGrid v7+ selection model shape (not a plain id array). */
const emptyRowSelection = (): GridRowSelectionModel => ({
  type: 'include',
  ids: new Set(),
});

const AttributeTable: React.FC<AttributeTableProps> = ({
  open,
  onClose,
  height,
  hideHeader = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>(emptyRowSelection);
  const [validationError, setValidationError] = useState<string | null>(null);

  const theme = useTheme();
  const dispatch = useAppDispatch();

  // Undo/redo support
  const { canUndo, canRedo, handleUndo, handleRedo } = useAttributeUndoRedo(open);

  // Get selection state
  const selectedFeatures = useAppSelector(selectSelectedFeatures);

  // Get active layer and its features
  const activeLayerId = useAppSelector((state: RootState) => state.layers.activeLayerId);
  const activeLayer = useAppSelector((state: RootState) =>
    state.layers.layers.find((l) => l.id === activeLayerId)
  );

  // Determine which features to display
  const displayFeatures = useMemo(() => {
    if (selectedFeatures.length > 0 && activeLayer?.features) {
      // Show only selected features from the active layer
      const selectedIds = new Set(
        selectedFeatures.filter((sf) => sf.layerId === activeLayerId).map((sf) => sf.featureId)
      );
      return activeLayer.features.filter((f) => selectedIds.has(f.id));
    }
    // No selection — show all features in the active layer
    return activeLayer?.features ?? [];
  }, [selectedFeatures, activeLayer, activeLayerId]);

  // Build columns dynamically from feature properties
  const columns: GridColDef[] = useMemo(() => {
    if (displayFeatures.length === 0) return [];

    // Collect all unique property keys across all displayed features
    const propertyKeys = new Set<string>();
    for (const feature of displayFeatures) {
      if (feature.properties) {
        Object.keys(feature.properties).forEach((key) => propertyKeys.add(key));
      }
    }

    // Always include an ID column first
    const cols: GridColDef[] = [
      {
        field: '__fid',
        headerName: 'FID',
        width: 80,
        sortable: true,
        filterable: false,
        disableColumnMenu: true,
      },
    ];

    // Add a column for each property key
    for (const key of propertyKeys) {
      cols.push({
        field: key,
        headerName: key,
        flex: 1,
        minWidth: 120,
        sortable: true,
        filterable: true,
        editable: true,
      });
    }

    return cols;
  }, [displayFeatures]);

  // Build rows from features
  const rows: GridRowsProp = useMemo(() => {
    return displayFeatures.map((feature, index) => ({
      id: feature.id,
      __fid: index + 1,
      ...(feature.properties ?? {}),
    }));
  }, [displayFeatures]);

  // Handlers
  const handleSortModelChange = useCallback((model: GridSortModel) => {
    setSortModel(model);
  }, []);

  const handleFilterModelChange = useCallback((model: GridFilterModel) => {
    setFilterModel(model);
  }, []);

  const handleRowSelectionChange = useCallback((model: GridRowSelectionModel) => {
    // Guard against malformed models from older API shapes
    if (!model || typeof model !== 'object' || !('ids' in model) || !(model.ids instanceof Set)) {
      setRowSelectionModel(emptyRowSelection());
      return;
    }
    setRowSelectionModel({
      type: model.type === 'exclude' ? 'exclude' : 'include',
      ids: model.ids,
    });
  }, []);

  const toggleExpand = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  /**
   * Handle cell edit confirmation. When a user double-clicks a cell, edits the value,
   * and confirms (Enter or click away), this handler validates the data type and
   * dispatches the update to Redux. Records the edit in the undo/redo history.
   * Throws an error if validation fails, which triggers onProcessRowUpdateError.
   */
  const processRowUpdate = useCallback(
    (newRow: GridRowModel, oldRow: GridRowModel) => {
      if (!activeLayerId) return oldRow;

      // Find which fields changed
      for (const field of Object.keys(newRow)) {
        if (field === 'id' || field === '__fid') continue;
        if (newRow[field] !== oldRow[field]) {
          // Detect the expected data type from existing features
          const expectedType = detectFieldType(activeLayer?.features ?? [], field);

          // Validate and coerce the new value
          const validatedValue = validateAttributeValue(newRow[field], expectedType);

          // Record the edit in history before applying
          dispatch(
            recordEdit({
              layerId: activeLayerId,
              featureId: newRow.id as string,
              field,
              oldValue: oldRow[field],
              newValue: validatedValue,
              timestamp: Date.now(),
            })
          );

          dispatch(
            updateFeatureAttribute({
              layerId: activeLayerId,
              featureId: newRow.id as string,
              field,
              value: validatedValue,
            })
          );

          // Update the row with the coerced value (e.g., string "42" → number 42)
          newRow = { ...newRow, [field]: validatedValue };
        }
      }

      return newRow;
    },
    [activeLayerId, activeLayer, dispatch]
  );

  /**
   * Handle errors from processRowUpdate (validation failures).
   * Displays the error message in a Snackbar.
   */
  const handleProcessRowUpdateError = useCallback((error: Error) => {
    setValidationError(error.message);
  }, []);

  const handleCloseValidationError = useCallback(() => {
    setValidationError(null);
  }, []);

  // Compute panel height
  const panelHeight = height ?? (isExpanded ? TABLE_HEIGHT_EXPANDED : TABLE_HEIGHT_DEFAULT);

  if (!open) return null;

  const featureCountLabel =
    selectedFeatures.length > 0
      ? `${displayFeatures.length} of ${activeLayer?.features?.length ?? 0} features (filtered by selection)`
      : `${displayFeatures.length} features`;

  // Safe selection model for DataGrid v9 (requires .ids Set)
  const safeSelectionModel: GridRowSelectionModel =
    rowSelectionModel?.ids instanceof Set ? rowSelectionModel : emptyRowSelection();

  return (
    <Box
      data-testid="attribute-table-panel"
      sx={{
        // Dock to bottom of map canvas when used as floating panel
        ...(hideHeader
          ? {
              height: '100%',
              minHeight: 0,
            }
          : {
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 0,
              height: panelHeight,
              minHeight: TABLE_HEIGHT_COLLAPSED,
              zIndex: 50,
              boxShadow: '0 -4px 24px rgba(0,0,0,0.35)',
            }),
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: theme.palette.background.paper,
        borderTop: hideHeader ? 'none' : `1px solid ${theme.palette.divider}`,
        overflow: 'hidden',
      }}
    >
      {/* Panel Header */}
      {!hideHeader && (
        <Box
          sx={{
            height: TABLE_HEIGHT_COLLAPSED,
            minHeight: TABLE_HEIGHT_COLLAPSED,
            display: 'flex',
            alignItems: 'center',
            px: 1.5,
            gap: 1,
            backgroundColor: alpha(theme.palette.background.default, 0.5),
            borderBottom: `1px solid ${theme.palette.divider}`,
            flexShrink: 0,
          }}
        >
          <TableChartIcon sx={{ fontSize: 16, color: theme.palette.primary.main }} />
          <Typography
            sx={{
              color: theme.palette.text.secondary,
              fontSize: '0.72rem',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.8px',
            }}
          >
            Map Details Table (Spreadsheet View)
          </Typography>

          {activeLayer && (
            <>
              <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
              <Chip
                label={activeLayer.name}
                size="small"
                sx={{
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main,
                  fontSize: '0.68rem',
                  height: 20,
                }}
              />
            </>
          )}

          <Chip
            label={featureCountLabel}
            size="small"
            sx={{
              backgroundColor: alpha(theme.palette.action.hover, 0.05),
              color: theme.palette.text.secondary,
              fontSize: '0.65rem',
              height: 20,
            }}
          />

          {/* Spacer */}
          <Box sx={{ flex: 1 }} />

          {/* Undo/Redo buttons */}
          <Tooltip title="Undo (Ctrl+Z)" placement="top" arrow>
            <span>
              <IconButton
                size="small"
                onClick={handleUndo}
                disabled={!canUndo}
                data-testid="attribute-undo-button"
                sx={{
                  width: 24,
                  height: 24,
                  color: canUndo ? theme.palette.primary.main : theme.palette.text.disabled,
                  '&:hover': {
                    color: theme.palette.text.primary,
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
              >
                <UndoIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title="Redo (Ctrl+Y)" placement="top" arrow>
            <span>
              <IconButton
                size="small"
                onClick={handleRedo}
                disabled={!canRedo}
                data-testid="attribute-redo-button"
                sx={{
                  width: 24,
                  height: 24,
                  color: canRedo ? theme.palette.primary.main : theme.palette.text.disabled,
                  '&:hover': {
                    color: theme.palette.text.primary,
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
              >
                <RedoIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </span>
          </Tooltip>

          {/* Filter indicator */}
          {filterModel.items.length > 0 && (
            <Chip
              icon={<FilterListIcon sx={{ fontSize: 12 }} />}
              label={`${filterModel.items.length} filter${filterModel.items.length > 1 ? 's' : ''}`}
              size="small"
              sx={{
                backgroundColor: alpha(theme.palette.warning.main, 0.1),
                color: theme.palette.warning.main,
                fontSize: '0.65rem',
                height: 20,
                '& .MuiChip-icon': { color: theme.palette.warning.main },
              }}
            />
          )}

          {/* Expand/Collapse */}
          <Tooltip title={isExpanded ? 'Collapse' : 'Expand'} placement="top" arrow>
            <IconButton
              size="small"
              onClick={toggleExpand}
              sx={{
                width: 24,
                height: 24,
                color: theme.palette.text.secondary,
                '&:hover': {
                  color: theme.palette.text.primary,
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              {isExpanded ? (
                <CollapseIcon sx={{ fontSize: 16 }} />
              ) : (
                <ExpandIcon sx={{ fontSize: 16 }} />
              )}
            </IconButton>
          </Tooltip>

          {/* Close */}
          <Tooltip title="Close Attribute Table" placement="top" arrow>
            <IconButton
              size="small"
              onClick={onClose}
              data-testid="attribute-table-close"
              sx={{
                width: 24,
                height: 24,
                color: theme.palette.text.secondary,
                '&:hover': {
                  color: theme.palette.text.primary,
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <CloseIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {/* Data Grid */}
      <Box
        sx={{
          flex: 1,
          overflow: 'hidden',
          '& .MuiDataGrid-root': {
            border: 'none',
            color: theme.palette.text.primary,
            fontSize: '0.75rem',
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: alpha(theme.palette.background.default, 0.5),
              borderBottom: `1px solid ${theme.palette.divider}`,
              minHeight: '32px !important',
              maxHeight: '32px !important',
            },
            '& .MuiDataGrid-columnHeader': {
              color: theme.palette.text.secondary,
              fontSize: '0.7rem',
              fontWeight: 600,
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 600,
            },
            '& .MuiDataGrid-cell': {
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.05)}`,
              padding: '0 8px',
            },
            '& .MuiDataGrid-row': {
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.05),
              },
              '&.Mui-selected': {
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.15),
                },
              },
            },
            '& .MuiDataGrid-footerContainer': {
              borderTop: `1px solid ${theme.palette.divider}`,
              backgroundColor: alpha(theme.palette.background.default, 0.5),
              minHeight: '28px !important',
            },
            '& .MuiTablePagination-root': {
              color: theme.palette.text.secondary,
              fontSize: '0.68rem',
            },
            '& .MuiDataGrid-overlay': {
              backgroundColor: alpha(theme.palette.background.paper, 0.9),
              color: theme.palette.text.disabled,
            },
            '& .MuiCheckbox-root': {
              color: theme.palette.text.disabled,
              '&.Mui-checked': {
                color: theme.palette.primary.main,
              },
            },
          },
        }}
      >
        {displayFeatures.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: '#666',
              fontSize: '0.8rem',
            }}
            data-testid="attribute-table-empty"
          >
            {activeLayer ? 'No features to display' : 'Select a layer to view its attributes'}
          </Box>
        ) : (
          <DataGrid
            rows={rows}
            columns={columns}
            density="compact"
            sortModel={sortModel}
            onSortModelChange={handleSortModelChange}
            filterModel={filterModel}
            onFilterModelChange={handleFilterModelChange}
            rowSelectionModel={safeSelectionModel}
            onRowSelectionModelChange={handleRowSelectionChange}
            processRowUpdate={processRowUpdate}
            onProcessRowUpdateError={handleProcessRowUpdateError}
            checkboxSelection
            disableRowSelectionOnClick
            pageSizeOptions={[25, 50, 100]}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 25 },
              },
            }}
            getRowHeight={() => 28}
            columnHeaderHeight={32}
            sx={{ height: '100%' }}
            data-testid="attribute-table-grid"
          />
        )}
      </Box>

      {/* Validation Error Snackbar */}
      <Snackbar
        open={!!validationError}
        autoHideDuration={5000}
        onClose={handleCloseValidationError}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        data-testid="attribute-validation-error-snackbar"
      >
        <Alert
          onClose={handleCloseValidationError}
          severity="error"
          variant="filled"
          sx={{ width: '100%' }}
          data-testid="attribute-validation-error"
        >
          {validationError}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AttributeTable;
